import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, LoaderFunctionArgs, RouterProvider } from "react-router-dom";

import { ToastHost } from "./components/ToastHost.tsx";
import Companies from "./pages/companies/Companies.tsx";
import Home from "./pages/Home.tsx";
import Root from "./pages/Root.tsx";

// Import our custom CSS
import "bootstrap-icons/font/bootstrap-icons.css";
import "./scss/styles.scss";

import { QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "react-redux";
import { checkRoutePermissionLoader } from "./loaders/auth.tsx";
import { companiesLoader } from "./loaders/companies.tsx";
import { applyCustomerFromQuery } from "./loaders/customer-override.tsx";
import { loaderChangeHistory, loader as mailTemplateLoader } from "./loaders/mail-templates.tsx";
import { rootLoader } from "./loaders/root.tsx";
import { loaderTemplates, loaderTemplatesVerifyPathExistance } from "./loaders/templates.tsx";
import Cancellation from "./pages/Cancellation.tsx";
import CompanyDetail from "./pages/companies/CompanyDetail.tsx";
import Entities from "./pages/entities/Entities.tsx";
import Explanation from "./pages/mail-templates/Explanation.tsx";
import MailTemplateRoot from "./pages/mail-templates/Root.tsx";
import Template from "./pages/mail-templates/Template.tsx";
import TemplatingRoot from "./pages/templating/Root.tsx";
import Admin from "./pages/Admin.tsx";
import MailTemplateMatrix from "./pages/admin/MailTemplateMatrix.tsx";
import Unsubscribe from "./pages/Unsubscribe.tsx";
import store from "./store/index";
import { queryClient } from "./utils/http/queryClient.tsx";

const hostname = window.location.hostname;
const simulatedDomain = new URLSearchParams(window.location.search).get("domain");

function getDomain(): "unsubscribe" | "cancellation" | "watchtower" {
  if (hostname.startsWith("unsubscribe.") || simulatedDomain === "unsubscribe") return "unsubscribe";
  if (hostname.startsWith("cancellation.") || simulatedDomain === "cancellation") return "cancellation";
  return "watchtower";
}

function getRouter() {
  const domain = getDomain();

  if (domain === "unsubscribe") {
    return createBrowserRouter([
      { path: "*", element: <Unsubscribe /> },
    ]);
  }

  if (domain === "cancellation") {
    return createBrowserRouter([
      { path: "*", element: <Cancellation /> },
    ]);
  }

  return createBrowserRouter([
    {
      path: "/",
      element: <Root />,
      // errorElement: <ErrorPage />,
      id: "root",
      loader: rootLoader,
      children: [
        { index: true, element: <Home /> },
        {
          path: "companies",
          loader: async () => {
            const result = await checkRoutePermissionLoader(["companies-view"]);
            if (result != null) return result;
            return companiesLoader();
          },
          element: <Companies />,
          children: [
            {
              path: ":companyName",
              element: <CompanyDetail />,
            },
          ],
        },
        {
          path: "entities",
          loader: async () => await checkRoutePermissionLoader(["entity-mgmt"]),
          element: <Entities />,
        },
        {
          path: "admin",
          loader: () => checkRoutePermissionLoader(["token-copy", "admin-endpoints"]),
          element: <Admin />,
        },
        {
          path: "admin/mail-template-matrix",
          loader: () => checkRoutePermissionLoader(["token-copy", "admin-endpoints"]),
          element: <MailTemplateMatrix />,
        },
        {
          path: "templates/*",
          id: "templates",
          loader: async ({ params: p, request }: LoaderFunctionArgs) => {
            const path = p["*"] ?? "";
            const permission = await checkRoutePermissionLoader(["templates"]);
            if (permission) return permission;
            await applyCustomerFromQuery(request);
            return (
              (await loaderTemplatesVerifyPathExistance({ path })) || (await loaderTemplates({ path }))
            );
          },
          element: <TemplatingRoot />,
        },
        {
          path: "mail-templates",
          loader: async ({ request }: LoaderFunctionArgs) => {
            const permission = await checkRoutePermissionLoader(["templates"]);
            if (permission) return permission;
            await applyCustomerFromQuery(request);
            return null;
          },
          element: <MailTemplateRoot />,
          children: [
            {
              path: ":language",
              id: "mail-template-language",
              loader: async ({ params, request }) => await loaderChangeHistory({ params, request }),
              element: <Explanation />,
            },
            {
              path: ":language/:automation",
              id: "mail-template-automation",
              element: <Explanation />,
            },
            {
              path: ":language/:automation/:event",
              id: "mail-template-event",
              element: <Explanation />,
            },
            {
              path: ":language/:automation/:event/:action",
              id: "mail-template-action",
              element: <Explanation />,
            },
            {
              path: ":language/:automation/:event/:action/:target",
              id: "mail-template-target",
              element: <Explanation />,
            },
            {
              path: ":language/:automation/:event/:action/:target/:version",
              id: "mail-template-version",
              loader: async ({ params, request }) => await mailTemplateLoader({ params, request }),
              element: <Template />,
            },
          ],
        },
      ],
    },
  ]);
}

const router = getRouter();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ToastHost>
          <RouterProvider router={router} />
        </ToastHost>
      </QueryClientProvider>
    </Provider>
  </StrictMode>
);
